import { Field, ErrorMessage, useFormikContext } from "formik";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import { currencyOptions } from "../../utils/currencyOptions";
import { formatNumberWithCommas, processDecimalInput } from "../../utils/formatNumber";
import { minSelectableDate } from "../../utils/dateUtils";

// Loan type and repayment frequency options
const loanTypeOptions = [
  { value: "personal", label: "Personal" },
  { value: "education", label: "Education" },
  { value: "business", label: "Business" },
];

const repaymentFrequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
];

// Initial values
export const cashInitialValues = {
  loanPurposeType: "",
  lenderName: "",
  loanType: "",
  loanPurpose: "",
  originalAmount: "",
  currency: "",
  outstandingAmount: "",
  interestRate: "",
  repaymentFrequency: "",
  loanTerm: "",
  nextPaymentDate: null,
  maturityDate: null,
  loanStartDate: null,  
   name: "",
};

// Validation schema
export const cashValidationSchema = Yup.object().shape({
  name: Yup.string()
      .min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^(?=.*[A-Za-z])[A-Za-z0-9 .()\-]+$/,
        "Must contain at least one letter, allowed: letters, numbers, spaces, dot, hyphen, parentheses."
      )
      .required("Name is required"),
  loanPurposeType: Yup.string().required("Field is required")
  .min(3, "Min length: 3 characters")
  .max(50, "Max length: 50 characters")
  .matches(
    /^(?=.*[A-Za-z])[A-Za-z0-9 .\-]+$/,
    "Must contain at least one letter, allowed: letters, numbers, spaces, dot, hyphen."
  ),
  lenderName: Yup.string().required("Field is required")
  .min(2, "Min length: 2 characters")
  .max(50, "Max length: 50 characters")
  .matches(
    /^(?=.*[A-Za-z])[A-Za-z0-9 .&-]+$/,
    "Must contain at least one letter, allowed: letters, numbers, spaces, dot, hyphen, ampersand."
  ),
  loanType: Yup.string().required("Field is required"),
  loanPurpose: Yup.string().required("Field is required"),
  originalAmount: Yup.number()
    .typeError("Must be a positive number")
    .required("Field is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  currency: Yup.string().required("Currency is required"),
  outstandingAmount: Yup.number()
    .typeError("Must be a positive number")
    .required("Field is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater")
    .test(
      "outstanding-vs-original-amount",
      "Outstanding balance cannot be greater than the original amount.",
      function(value) {
        const { originalAmount } = this.parent;
        if (!value || !originalAmount) return true;
        return Number(value) <= Number(originalAmount);
      }
    ),
  interestRate: Yup.number()
    .typeError("Must be a number")
    .required("Interest rate is required")
    .min(0, "Must be a positive number")
    .max(100, "Must be a number between 0 and 100")
    .test(
      "is-decimal",
      "Must have up to 2 decimal places",
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split(".")[1];
        return !decimalPlaces || decimalPlaces.length <= 2;
      }
    ),
  repaymentFrequency: Yup.string().required("Field is required"),
  loanTerm: Yup.number()
  .typeError("Must be a number")
    .required("Loan term is required")
    .positive("Must be greater than 0"),
  nextPaymentDate: Yup.date().nullable().required("Field is required").test(
    "nextPaymentDate-validation",
    "Next payment date must be after the loan start date.",
    function(value) {
      const { loanStartDate } = this.parent;
      if (!value || !loanStartDate) return true;
      return new Date(value) > new Date(loanStartDate);
    }
  ),
  maturityDate: Yup.date().nullable().required("Field is required").test(
    "maturityDate-start-validation",
    "Maturity date must be after the loan start date.",
    function(value) {
      const { loanStartDate } = this.parent;
      if (!value || !loanStartDate) return true;
      return new Date(value) > new Date(loanStartDate);
    }
  ),
  loanStartDate: Yup.date().nullable().required("Field is required").test(
    "loanStartDate-past-validation",
    "Loan start date must be today or a past date.",
    function(value) {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const loanStart = new Date(value);
      loanStart.setHours(0, 0, 0, 0);
      return loanStart <= today;
    }
  ),
});

// Component
const LoanForm = ({ values, setFieldValue, errors, touched, localReadOnly }) => {
  // Blur only marks the field touched; writing values.x back used a stale render value (ML-30).
  const { setFieldTouched } = useFormikContext();
  return (
    <>
     <div className="col-md-12 mb-4">
            <label className="form-label">
              Liability Name<span className="text-danger">*</span>
            </label>
            <Field
              type="text"
              name="name"
              placeholder="e.g., Loan, Personal Loan"
             readOnly={localReadOnly}
             isDisabled={localReadOnly}
              className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
            />
            <ErrorMessage name="name" component="div" className="invalid-feedback" />
          </div>
    <div className="row">
      {/* Loan Purpose/Type */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Purpose/Type<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="loanPurposeType"
          placeholder="e.g., Home Improvement"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.loanPurposeType && touched.loanPurposeType ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="loanPurposeType" component="div" className="invalid-feedback" />
      </div>

      {/* Lender Name */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Lender Name<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="lenderName"
          placeholder="e.g., Bank or Institution"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.lenderName && touched.lenderName ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="lenderName" component="div" className="invalid-feedback" />
      </div>

      {/* Loan Type */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Type<span className="text-danger">*</span></label>
        <Select
          options={loanTypeOptions}
          value={loanTypeOptions.find((opt) => opt.value === values.loanType)}
          onChange={(option) => setFieldValue("loanType", option.value)}
          classNamePrefix="react-select"
readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        {errors.loanType && touched.loanType && <div className="text-danger small">{errors.loanType}</div>}
      </div>

      {/* Loan Purpose */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Purpose<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="loanPurpose"
          placeholder="e.g., Debt Consolidation"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.loanPurpose && touched.loanPurpose ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="loanPurpose" component="div" className="invalid-feedback" />
      </div>

      {/* Original Amount with Currency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Original Amount<span className="text-danger">*</span></label>
        <div className="input-group">
          <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.currency)}
            onChange={option => setFieldValue("currency", option.value)}
            className="currency-select"
            classNamePrefix="react-select"
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
          />
          <Field
            type="text"
            name="originalAmount"
            placeholder="e.g., 50,000"
            readOnly={localReadOnly}
             isDisabled={localReadOnly}
            className={`form-control ${errors.originalAmount && touched.originalAmount ? "is-invalid" : ""}`}
            value={values.originalAmount ? formatNumberWithCommas(values.originalAmount) : ""}
            onChange={(e) => {
                const unformatted = processDecimalInput(e.target.value)
                setFieldValue("originalAmount", unformatted || "", true)
                // Trigger validation for dependent field
                setTimeout(() => {
                  setFieldValue("outstandingAmount", values.outstandingAmount, true);
                }, 0);
              }}
              onPaste={(e) => {
                e.preventDefault()
                const unformatted = processDecimalInput(e.clipboardData.getData('text'))
                setFieldValue("originalAmount", unformatted || "", true)
                setTimeout(() => {
                  setFieldValue("outstandingAmount", values.outstandingAmount, true);
                }, 0);
              }}
            onBlur={(e) => {
              const unformatted = e.target.value.replace(/,/g, "")
              if (unformatted) {
                setFieldValue("originalAmount", unformatted)
              }
            }}
          />
        </div>
        <ErrorMessage name="originalAmount" component="div" className="invalid-feedback" />
        <ErrorMessage name="currency" component="div" className="invalid-feedback" />
      </div>

      {/* Loan Start Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Start Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.loanStartDate ? new Date(values.loanStartDate) : null}
          onChange={(date) => setFieldValue("loanStartDate", date, true)}
          onBlur={() => setFieldTouched("loanStartDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${errors.loanStartDate && touched.loanStartDate ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          maxDate={new Date()}
          renderCustomHeader={({
            date,
            changeMonth,
            changeYear,
          }) => {
            const today = new Date();
            const currentYear = today.getFullYear();
            const currentMonth = today.getMonth();
            const selectedYear = date && date.getFullYear ? date.getFullYear() : currentYear;

            const years = Array.from(
              { length: currentYear - 1900 + 1 },
              (_, i) => currentYear - i
            );

            const months = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];

            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <select
                  value={date && date.getMonth ? date.getMonth() : currentMonth}
                  onChange={(e) =>
                    changeMonth(Number(e.target.value))
                  }
                >
                  {months.map((month, index) => {
                    const disableMonth =
                      selectedYear > currentYear ||
                      (selectedYear === currentYear &&
                        index > currentMonth);

                    return (
                      <option
                        key={month}
                        value={index}
                        disabled={disableMonth}
                      >
                        {month}
                      </option>
                    );
                  })}
                </select>

                <select
                  value={selectedYear}
                  onChange={(e) =>
                    changeYear(Number(e.target.value))
                  }
                >
                  {years.map((year) => (
                    <option
                      key={year}
                      value={year}
                      disabled={year > currentYear}
                    >
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            );
          }}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="loanStartDate" component="div" className="invalid-feedback" />
      </div>

      {/* Outstanding Balance */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Outstanding Balance<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="outstandingAmount"
          placeholder="e.g., 15,000"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.outstandingAmount && touched.outstandingAmount ? "is-invalid" : ""}`}
          value={values.outstandingAmount ? formatNumberWithCommas(values.outstandingAmount) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            // Allow only one decimal point and limit to 2 decimal places
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("outstandingAmount", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("outstandingAmount", unformatted)
            }
          }}
        />
        <ErrorMessage name="outstandingAmount" component="div" className="invalid-feedback" />
      </div>

      {/* Interest Rate */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Interest Rate (%)<span className="text-danger">*</span></label>
        <Field
          type="number"
          name="interestRate"
          placeholder="e.g., 5.5"
         readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.interestRate && touched.interestRate ? "is-invalid" : ""}`}
          step="0.01"
        />
        <ErrorMessage name="interestRate" component="div" className="invalid-feedback" />
      </div>

      {/* Repayment Frequency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Repayment Frequency<span className="text-danger">*</span></label>
        <Select
          options={repaymentFrequencyOptions}
          value={repaymentFrequencyOptions.find((opt) => opt.value === values.repaymentFrequency)}
          onChange={(option) => setFieldValue("repaymentFrequency", option.value)}
          classNamePrefix="react-select"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        {errors.repaymentFrequency && touched.repaymentFrequency && (
          <div className="text-danger small">{errors.repaymentFrequency}</div>
        )}
      </div>

      {/* Loan Term */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Term<span className="text-danger">*</span></label>
        <Field
          type="number"
          name="loanTerm"
          placeholder="e.g., 5 years"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.loanTerm && touched.loanTerm ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="loanTerm" component="div" className="invalid-feedback" />
      </div>

      {/* Next Payment Date with DatePicker */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Next Payment Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.nextPaymentDate ? new Date(values.nextPaymentDate) : null}
          onChange={(date) => setFieldValue("nextPaymentDate", date, true)}
          onBlur={() => setFieldTouched("nextPaymentDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${errors.nextPaymentDate && touched.nextPaymentDate ? "is-invalid" : ""}`}
          placeholderText="Select date"
          minDate={minSelectableDate(values.loanStartDate)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
        />
        <ErrorMessage name="nextPaymentDate" component="div" className="invalid-feedback" />
      </div>

      {/* Maturity Date with DatePicker */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Maturity Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.maturityDate ? new Date(values.maturityDate) : null}
          onChange={(date) => setFieldValue("maturityDate", date, true)}
          onBlur={() => setFieldTouched("maturityDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${errors.maturityDate && touched.maturityDate ? "is-invalid" : ""}`}
          placeholderText="Select date"
          minDate={minSelectableDate(values.loanStartDate, values.nextPaymentDate)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
        />
        <ErrorMessage name="maturityDate" component="div" className="invalid-feedback" />
      </div>
    </div>
    </>
  );
};

export default LoanForm;
